import { useAuth0 } from "@auth0/auth0-react";
import { ChevronDownIcon } from "assets/icons/ChevronDown";
import { LogoutIcon } from "assets/icons/Logout";
import { UserIcon } from "assets/icons/User";
import { UserRole, userRoleMapping } from "constants/userRole";
import { OverlayPanel } from "primereact/overlaypanel";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { userSelector } from "store/slices/user";

import {
  AvatarStyled,
  ChevronStyled,
  DividerStyled,
  LogoutItemStyled,
  MenuItemStyled,
  PanelStyled,
  RoleStyled,
  TextContainer,
  TriggerStyled,
  UserStyled,
} from "./styled";

const getInitials = (name: string | undefined) => {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";

  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : "";

  return `${first}${last}`.toUpperCase();
};

const transformScope = (scope: string | undefined) => {
  if (!scope) return "Not Identified";

  return (
    userRoleMapping[scope as UserRole] ||
    scope.replace(/([A-Z])/g, " $1").trim()
  );
};

export function UserDropdown() {
  const panel = useRef<OverlayPanel>(null);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const { isAuthenticated, logout } = useAuth0();
  const { scope, name, email } = useSelector(userSelector);

  if (!isAuthenticated) {
    return <div />;
  }

  const initials = getInitials(name);

  const closeAnd = (action: () => void) => () => {
    panel.current?.hide();
    action();
  };

  return (
    <>
      <TriggerStyled
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label="User menu"
        onClick={(e) => panel.current?.toggle(e)}
      >
        <AvatarStyled aria-hidden="true">
          {initials || <i className="pi pi-user" />}
        </AvatarStyled>

        <TextContainer>
          <UserStyled>{name}</UserStyled>
          <RoleStyled>{transformScope(scope)}</RoleStyled>
        </TextContainer>

        <ChevronStyled $open={isOpen} aria-hidden="true">
          <ChevronDownIcon />
        </ChevronStyled>
      </TriggerStyled>

      <PanelStyled
        ref={panel}
        onShow={() => setIsOpen(true)}
        onHide={() => setIsOpen(false)}
      >
        <div className="flex flex-column gap-1 pt-1 px-2">
          <span
            className="text-base font-semibold"
            style={{ color: "var(--brand-black)" }}
          >
            {name}
          </span>
          <span
            className="text-sm"
            style={{
              color: "var(--brand-gray-600)",
              overflowWrap: "anywhere",
            }}
          >
            {email}
          </span>
        </div>

        <DividerStyled />

        <div className="flex flex-column">
          <MenuItemStyled
            type="button"
            onClick={closeAnd(() => navigate("/account"))}
          >
            <UserIcon aria-hidden="true" />
            <span>Profile</span>
          </MenuItemStyled>
        </div>

        <DividerStyled />

        <div className="flex flex-column">
          <LogoutItemStyled
            type="button"
            $danger
            onClick={closeAnd(() => logout())}
          >
            <LogoutIcon aria-hidden="true" />
            <span>Log out</span>
          </LogoutItemStyled>
        </div>
      </PanelStyled>
    </>
  );
}
